import React, { useCallback, useMemo } from 'react'
import { Popconfirm, Spin } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DownOutlined, ReloadOutlined } from '@ant-design/icons'
import { Table } from '@surecompanies/core_components'
import { useGetGeneratedInvoiceVersionsQuery } from '../../../api/generated-invoice-api-client'
import { FundingInvoiceListItemDto } from '../../../api/dto/generated-invoice.dto'
import { TableCard } from '../InvoiceDeliveryTable.styles'
import {
  EmptyText,
  ExpandedAmount,
  ExpandedContainer,
  ExpandedList,
  ExpandedMeta,
  ExpandedRow,
  ExpandedTitle,
  PeriodCell,
  RegenerateButton,
  StatusBadge,
  VersionBadge,
  VersionsText,
} from './InvoiceGenerationView.styles'
import { formatCurrency, formatStatusLabel } from './utils'

interface GeneratedInvoiceTableProps {
  invoices: FundingInvoiceListItemDto[]
  isLoading: boolean
  expandedRowKeys: React.Key[]
  onExpandedRowKeysChange: (keys: React.Key[]) => void
  onOpenDetail: (invoice: FundingInvoiceListItemDto) => void
  onRegenerate: (invoice: FundingInvoiceListItemDto) => void
  isRegenerating: boolean
  regeneratingInvoiceKey?: string
}

interface GeneratedInvoiceVersionsRowsProps {
  invoiceId: string
  onOpenDetail: (invoice: FundingInvoiceListItemDto) => void
}

const GeneratedInvoiceVersionsRows: React.FC<
  GeneratedInvoiceVersionsRowsProps
> = ({ invoiceId, onOpenDetail }) => {
  const { data, isLoading } = useGetGeneratedInvoiceVersionsQuery(invoiceId)
  const versions = (data?.versions ?? []).filter(
    (item) => item.id !== invoiceId
  )

  if (isLoading) {
    return (
      <ExpandedContainer>
        <Spin size="small" />
      </ExpandedContainer>
    )
  }

  return (
    <ExpandedContainer>
      <ExpandedTitle>Historical versions</ExpandedTitle>
      {versions.length === 0 ? (
        <EmptyText>No historical versions for this invoice thread.</EmptyText>
      ) : (
        <ExpandedList>
          {versions.map((version) => (
            <ExpandedRow key={version.id} onClick={() => onOpenDetail(version)}>
              <VersionBadge>v{version.version}</VersionBadge>
              <ExpandedMeta>
                {version.issueDate} · {version.period}
              </ExpandedMeta>
              <ExpandedAmount>{formatCurrency(version.amount)}</ExpandedAmount>
              <StatusBadge>{formatStatusLabel(version.status)}</StatusBadge>
            </ExpandedRow>
          ))}
        </ExpandedList>
      )}
    </ExpandedContainer>
  )
}

const GeneratedInvoiceTable: React.FC<GeneratedInvoiceTableProps> = ({
  invoices,
  isLoading,
  expandedRowKeys,
  onExpandedRowKeysChange,
  onOpenDetail,
  onRegenerate,
  isRegenerating,
  regeneratingInvoiceKey,
}) => {
  const toggleVersions = useCallback(
    (recordId: string) => {
      const isExpanded = expandedRowKeys.includes(recordId)
      onExpandedRowKeysChange(
        isExpanded
          ? expandedRowKeys.filter((key) => key !== recordId)
          : [...expandedRowKeys, recordId]
      )
    },
    [expandedRowKeys, onExpandedRowKeysChange]
  )

  const columns: ColumnsType<FundingInvoiceListItemDto> = useMemo(
    () => [
      {
        title: 'Company',
        key: 'clientName',
        dataIndex: 'clientName',
        width: '22%',
        ellipsis: true,
        render: (value: string | null) => value ?? '—',
      },
      {
        title: 'Billing Month',
        key: 'period',
        width: '22%',
        render: (_, record) => {
          const isExpanded = expandedRowKeys.includes(record.id)
          return (
            <PeriodCell>
              <span>{record.period}</span>
              {record.versionCount > 1 && (
                <VersionsText
                  type="button"
                  data-expanded={isExpanded}
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleVersions(record.id)
                  }}
                >
                  {record.versionCount} versions
                  <DownOutlined />
                </VersionsText>
              )}
            </PeriodCell>
          )
        },
      },
      {
        title: 'Issue Date',
        key: 'issueDate',
        dataIndex: 'issueDate',
        width: '22%',
      },
      {
        title: 'Amount',
        key: 'amount',
        dataIndex: 'amount',
        width: '18%',
        align: 'right',
        render: (value: number) => formatCurrency(value),
      },
      {
        title: 'Status',
        key: 'status',
        width: '14%',
        render: (_, record) => (
          <StatusBadge>{formatStatusLabel(record.status)}</StatusBadge>
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: '16%',
        render: (_, record) => {
          const invoiceKey = `${record.clientId}:${record.billingPeriodStart}`
          const isPending =
            isRegenerating && regeneratingInvoiceKey === invoiceKey

          return (
            <Popconfirm
              title="Regenerate invoice"
              description="This creates a new version for this client and funded month."
              onConfirm={() => onRegenerate(record)}
              okText="Regenerate"
              cancelText="Cancel"
            >
              <RegenerateButton
                onClick={(e) => e.stopPropagation()}
                disabled={isPending}
              >
                <ReloadOutlined spin={isPending} /> Regenerate
              </RegenerateButton>
            </Popconfirm>
          )
        },
      },
    ],
    [
      expandedRowKeys,
      isRegenerating,
      onRegenerate,
      regeneratingInvoiceKey,
      toggleVersions,
    ]
  )

  return (
    <TableCard>
      <Table
        rowKey="id"
        dataSource={invoices}
        columns={columns}
        loading={isLoading}
        pagination={false}
        scroll={{ y: 560, x: 'max-content' }}
        expandable={{
          showExpandColumn: false,
          rowExpandable: (record) => record.versionCount > 1,
          expandedRowKeys,
          onExpandedRowsChange: (keys) => onExpandedRowKeysChange([...keys]),
          expandedRowRender: (record) => (
            <GeneratedInvoiceVersionsRows
              invoiceId={record.id}
              onOpenDetail={onOpenDetail}
            />
          ),
          expandRowByClick: false,
        }}
        onRow={(record) => ({
          style: { cursor: 'pointer' },
          onClick: (e) => {
            const target = e.target as HTMLElement
            if (
              target.closest('button') ||
              target.closest('.ant-popover') ||
              target.closest('.ant-table-row-expand-icon')
            ) {
              return
            }
            onOpenDetail(record)
          },
        })}
      />
    </TableCard>
  )
}

export { GeneratedInvoiceTable }
